import { use, useRef, useState, useEffect } from 'react';
import axios from '../api/axios';
import { updateCampaignCustomisationVarsRoute, uploadCustomAssetRoute } from '../api/routes';
import DEBUG_LOGGER from '../helpers/DebugLogger';
import { findFeatureData } from '../components/v2/CustomisationWorkspace/v2IntegrationHelpers/CampaignAndFeatureDataHelperFunctions';
import { validateValAtIndex } from '../components/CustomisationControls/CustomisationControls';
import { CUSTOMISED_URL_MAP } from '../config/FeatureDashboardVarMap';
import { stripTemplateAssetSourceMetadata } from '../helpers/FeatureAssets';

/**
 * Custom hook to manage state for saving feature customisations.
 * 
 * @param {object} toast - Toast notification object for displaying messages.
 * 
 * Returns a function we can call to trigger the save process, along with state
 * variables to track loading and error states.
 * 
 * When we trigger a save, the save is delayed unless we pass in a different feature id to the last save,
 * in which case the save for the previous id happens immediately, while the new id is queued for saving after a short delay.
 * 
 * A second returned function allows us to force an immediate save of any queued feature customisation.
 */
export const useFeatureCustomisationSaver = (campaignId, campaignData, customisationData, toast = null, delay = 1000, simulatedSave = false, options = {}) => {
    const onSaveSuccess = options?.onSaveSuccess;
    const [isSaving, setIsSaving] = useState(false);
    const isSavingRef = useRef(isSaving);
    useEffect(() => {
        isSavingRef.current = isSaving;
    }, [isSaving]);
    const [saveError, setSaveError] = useState(null);
    const [lastSavedFeatureId, setLastSavedFeatureId] = useState(null);
    const [queuedFeatureId, setQueuedFeatureId] = useState(null);
    const queueFeatureIdRef = useRef(queuedFeatureId);
    useEffect(() => {
        queueFeatureIdRef.current = queuedFeatureId;
    }, [queuedFeatureId]);
    const [queuedCustomisationData, setQueuedCustomisationData] = useState(null);
    const queueCustomisationDataRef = useRef(queuedCustomisationData);
    useEffect(() => {
        queueCustomisationDataRef.current = queuedCustomisationData;
    }, [queuedCustomisationData]);
    const [queuedFeatureData, setQueuedFeatureData] = useState(null);
    const queueFeatureDataRef = useRef(queuedFeatureData);
    useEffect(() => {
        queueFeatureDataRef.current = queuedFeatureData;
    }, [queuedFeatureData]);

    const saveTimeoutRef = useRef(null);
    const hasUnsavedChanges = () => saveTimeoutRef.current !== null;

    /* uploadFiles function and saveData function taken from the old CustomiseFeature.jsx container component and adapted slightly */

    const generateUniqueFilename = (originalFile) => {
        const timestamp = Date.now();
        const randomNum = Math.floor(Math.random() * 100000);
        const fileExtension = originalFile.name.split('.').pop();
        const originalNameWithoutExt = originalFile.name.substring(0, originalFile.name.lastIndexOf('.')) || originalFile.name;
        const sanitizedNameWithoutExt = originalNameWithoutExt.replace(/[^\w]+/g, '');
        const resolvedBaseName = sanitizedNameWithoutExt || 'file';
        const uniqueFilename = `${resolvedBaseName}__${timestamp}_${randomNum}.${fileExtension}`;
        originalFile.uniqueFilename = uniqueFilename;
        return uniqueFilename;
    }

    // An iterative function that handles uploading files one at a time from an array, then calls a callback when done
    const uploadFiles = async (featureId, filesArray, fileDirs, totalFiles, afterUploadCallback, filesUploaded = 0) => {
        // setUploadedCount(filesUploaded);
        if (filesArray.length > 0) {
            const fileToUpload = filesArray.pop();
            const fileDir = fileDirs.pop();
            // setCurrentUploadFilename(fileToUpload.name);

            if (fileToUpload?.alreadySaved === true) {
                console.log('File already saved, skipping upload: ', fileToUpload, fileDir);
                await uploadFiles(featureId, filesArray, fileDirs, totalFiles, afterUploadCallback, filesUploaded + 1);
                return;
            }

            console.log('Uploading file (' + filesUploaded + 1 + '/' + totalFiles + '): ', fileToUpload, fileDir);

            /* fileToUpload.customAssetData = {
                featureID: featureId,
                type: fileDir,
            }; */
            const formData = new FormData();

            formData.append('filename', fileToUpload?.uniqueFilename ?? generateUniqueFilename(fileToUpload));
            formData.append('campaignID', campaignId);
            formData.append('featureID', featureId);
            /* if (streamId) {
                formData.append('streamID', streamId);
            } */
            formData.append('type', fileDir);
            formData.append('file', fileToUpload);
            await axios.post(uploadCustomAssetRoute, formData, { withCredentials: true })
                .then(async res => {
                    console.log('Upload result: ', res);
                    fileToUpload.alreadySaved = true;
                    await uploadFiles(featureId, filesArray, fileDirs, totalFiles, afterUploadCallback, filesUploaded + 1);
                })
                .catch(async err => {
                    DEBUG_LOGGER.log('Upload error: ', err);
                    toast.error('Error Uploading File - ' + err);
                    if (afterUploadCallback) {
                        await afterUploadCallback(false);
                    }
                });
        } else {
            if (afterUploadCallback) {

                setTimeout(() => { afterUploadCallback(true) }, 500);
            }
        }
    }

    const saveData = async (featureId, afterSaveCallback = null, ignoreUpload = false, useCampaignData = null, useCustomisationData = null) => {
        // if(customisationData) customisationData = customisationData;
        useCustomisationData = useCustomisationData ?? customisationData;
        const resolvedCustomisationData = useCustomisationData?.current
            ? useCustomisationData
            : { current: { [featureId]: useCustomisationData || {} } };
        // console.log('Save changes: ', useCustomisationData, ignoreUpload, featureId);
        const dataSchema = findFeatureData(useCampaignData ?? campaignData, featureId).feature.customisation;
        // console.log('Data schema: ', dataSchema);
        // console.log('Using campaign data: ', useCampaignData ?? campaignData);

        // Store any files to upload in an array
        const filesToUpload = [];
        const fileDirs = [];
        const oldFilenames = [];
        const filesToRemove = [];
        // Rebuild the data object and send up to the server...
        const rebuiltDataStructure = {};
        for (let i = 0; i < dataSchema.length; i++) {
            // Tab groups
            const tabKey = dataSchema[i].tabKey;
            console.log('TAB: ', tabKey);
            for (let j = 0; j < dataSchema[i].options.length; j++) {
                // Tab group options
                const varKey = dataSchema[i].options[j].varKey;
                console.log('VARKEY: ', varKey);
                for (let k = 0; k < dataSchema[i].options[j].variables.length; k++) {
                    const varName = dataSchema[i].options[j].variables[k].varName;
                    const variableSchema = dataSchema[i].options[j].variables[k];
                    const liveVarValue = useCustomisationData?.current?.[featureId]?.[tabKey]?.[varKey]?.[varName];
                    const resolvedLiveVarValue = resolvedCustomisationData?.current?.[featureId]?.[tabKey]?.[varKey]?.[varName];
                    const hasObjectValueSchema = variableSchema?.valSchema && typeof variableSchema.valSchema === 'object';
                    console.log('VARNAME: ', varName);
                    // We now have all the keys to build our data structure
                    if (rebuiltDataStructure[tabKey] === undefined) {
                        rebuiltDataStructure[tabKey] = {};
                    }
                    if (rebuiltDataStructure[tabKey][varKey] === undefined) {
                        rebuiltDataStructure[tabKey][varKey] = {};
                    }

                    // Simple vars like loop/volume are stored as scalars, not arrays of schema objects.
                    if (!hasObjectValueSchema) {
                        rebuiltDataStructure[tabKey][varKey][varName] = resolvedLiveVarValue;
                        continue;
                    }

                    if (rebuiltDataStructure[tabKey][varKey][varName] === undefined) {
                        rebuiltDataStructure[tabKey][varKey][varName] = [];
                    }
                    const liveVarArray = Array.isArray(resolvedLiveVarValue) ? resolvedLiveVarValue : [];
                    // Drop any invalid data (maybe they forgot to select a file)...
                    // Note: this while loop was commented out in the original code
                    /* while (customisationData.current[featureId][tabKey][varKey][varName].length > 0 && validateLastVal(customisationData.current[featureId][tabKey][varKey][varName], dataSchema[i].options[j].variables[k].valSchema) === false) {
                        customisationData.current[featureId][tabKey][varKey][varName].pop();
                    } */
                    // While loop and pop doesn't do the trick - what if I add 3 values and the middle one is valid, the last one will be removed, but the other invalid one won't
                    // Before we go splicing out data, this only applies to data where multiples are allowed...
                    if (variableSchema.allowMultiple) {
                        for (let validate_i = liveVarArray.length - 1; validate_i >= 0; validate_i--) {
                            if (validateValAtIndex(liveVarArray, variableSchema.valSchema, validate_i) === false) {
                                // console.log('Removing invalid val at index: ', validate_i, useCustomisationData.current[featureId][tabKey][varKey][varName][validate_i]);
                                liveVarArray.splice(validate_i, 1);
                            }
                        }
                    }
                    // Now add in our new data, being careful to only send updated vars and preserve isDefault
                    for (let l = 0; l < liveVarArray.length; l++) {
                        // This if was also commented out in the original code
                        // if (useCustomisationData.current[featureId][tabKey][varKey][varName][l].updated) {
                        const newDataObj = {};
                        for (let prop in variableSchema.valSchema) {
                            console.log('PROP: ', prop, liveVarArray[l][prop]);

                            if (CUSTOMISED_URL_MAP.hasOwnProperty(variableSchema.valSchema[prop].type.toLowerCase())) {
                                newDataObj[prop] = { ...liveVarArray[l][prop] };
                                if (liveVarArray[l][prop].newFile) {
                                    fileDirs.push(CUSTOMISED_URL_MAP[variableSchema.valSchema[prop].type.toLowerCase()].dir);
                                    filesToUpload.push(liveVarArray[l][prop].newFile);
                                    if (liveVarArray[l][prop].originalFile) {
                                        oldFilenames.push(liveVarArray[l][prop].originalFile);
                                        filesToRemove.push(CUSTOMISED_URL_MAP[variableSchema.valSchema[prop].type.toLowerCase()].dir + '/' + liveVarArray[l][prop].originalFile);
                                    }
                                    newDataObj[prop].isStreamLevel = typeof streamId !== 'undefined';
                                }
                                // Get rid of any stuff we don't need in the db
                                newDataObj[prop].originalFile = newDataObj[prop].filename;
                                if (newDataObj[prop].newFile) {
                                    newDataObj[prop].filename = newDataObj[prop].newFile?.uniqueFilename ?? generateUniqueFilename(newDataObj[prop].newFile);
                                    stripTemplateAssetSourceMetadata(newDataObj[prop]);
                                }
                                newDataObj[prop].srcUrl = undefined;
                                newDataObj[prop].newFile = undefined;
                            } else {
                                newDataObj[prop] = liveVarArray[l][prop];
                            }
                        }

                        newDataObj.isDefault = liveVarArray[l]["isDefault"];
                        const livePlaySFX = liveVarArray[l]["playSFX"];
                        if (livePlaySFX !== undefined) { newDataObj.playSFX = livePlaySFX; }
                        rebuiltDataStructure[tabKey][varKey][varName].push(newDataObj);
                        // }
                    }
                }
            }
        }
        console.log('Built data to send: ', rebuiltDataStructure);

        const dataToSend = {};
        dataToSend[featureId] = rebuiltDataStructure;

        // Now we need to iterate through our theme data and process any files that need uploading or removing
        /* if (themeDataRef.current) {
            for (let i = 0; i < themeDataRef.current.length; i++) {
                const oneThemeData = themeDataRef.current[i].themeData;
                for (let tab in oneThemeData) {
                    if (oneThemeData[tab])
                    for (let varKey in oneThemeData[tab]) {
                        if (oneThemeData[tab][varKey])
                        for (let varName in oneThemeData[tab][varKey]) {
                            if (oneThemeData[tab][varKey][varName] && oneThemeData[tab][varKey][varName].length)
                            for (let varIndex = 0; varIndex < oneThemeData[tab][varKey][varName].length; varIndex++) {
                                for (let prop in oneThemeData[tab][varKey][varName][varIndex]) {
                                    console.log('PROP: ', prop, oneThemeData[tab][varKey][varName][varIndex][prop]);
                                    if (oneThemeData?.[tab]?.[varKey]?.[varName]?.[varIndex]?.[prop]?.type && CUSTOMISED_URL_MAP.hasOwnProperty(oneThemeData[tab][varKey][varName][varIndex][prop].type?.toLowerCase())) {
                                        if (oneThemeData[tab][varKey][varName][varIndex][prop].newFile) {
                                            console.log('>>>> THEME FILE UPLOAD: ', CUSTOMISED_URL_MAP[oneThemeData[tab][varKey][varName][varIndex][prop].type.toLowerCase()].dir, oneThemeData[tab][varKey][varName][varIndex][prop].newFile)
                                            fileDirs.push(CUSTOMISED_URL_MAP[oneThemeData[tab][varKey][varName][varIndex][prop].type.toLowerCase()].dir);
                                            filesToUpload.push(oneThemeData[tab][varKey][varName][varIndex][prop].newFile);
                                            if (oneThemeData[tab][varKey][varName][varIndex][prop].originalFile) {
                                                console.log('>>>> THEME FILE REMOVE: ', oneThemeData[tab][varKey][varName][varIndex][prop].originalFile, CUSTOMISED_URL_MAP[oneThemeData[tab][varKey][varName][varIndex][prop].type.toLowerCase()].dir + '/' + oneThemeData[tab][varKey][varName][varIndex][prop].originalFile);
                                                oldFilenames.push(oneThemeData[tab][varKey][varName][varIndex][prop].originalFile);
                                                filesToRemove.push(CUSTOMISED_URL_MAP[oneThemeData[tab][varKey][varName][varIndex][prop].type.toLowerCase()].dir + '/' + oneThemeData[tab][varKey][varName][varIndex][prop].originalFile);
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        } */

        // Add in any removed files to our files to remove list
        /* for (let i = 0; i < removedFiles.current.length; i++) {
            oldFilenames.push(removedFiles.current[i]);
            filesToRemove.push(removedFileTypes.current[i] + '/' + removedFiles.current[i]);
        } */
        console.log('Files: ', filesToUpload, fileDirs, oldFilenames);
        // Create a list of old files to be removed by splicing out any original files that have the same name as a new file
        /* for (let i = 0; i < filesToUpload.length; i++) {
            while (oldFilenames.indexOf(filesToUpload[i].uniqueFilename) >= 0) {
                oldFilenames.splice(oldFilenames.indexOf(filesToUpload[i].uniqueFilename), 1);
                filesToRemove.splice(oldFilenames.indexOf(filesToUpload[i].uniqueFilename), 1);
            }
        } */
        console.log('Files to remove: ', filesToRemove);

        // Idea now is to upload each file one at a time so we can show the progress and then update the db afterwards
        const afterUploadCallback = async (success) => {
            console.log('All uploads complete, success: ', success);
            if (success) {
                // After successfully uploading our files, send the data to the db...
                console.log('Sending updated data to server: ', dataToSend, campaignId, featureId /* , streamId */);
                await axios
                    .post(/* streamId ? updateStreamCustomisationVarsRoute : */ updateCampaignCustomisationVarsRoute, {
                        campaignID: campaignId,
                        // streamID: streamId, 
                        featureID: featureId,
                        updatedVars: dataToSend,
                        oldFiles: [], // filesToRemove,
                        withCredentials: true
                    })
                    .then(async function (response) {
                        await onSaveSuccess?.({
                            campaignId,
                            featureId,
                            savedCurrentData: rebuiltDataStructure,
                            response: response?.data,
                        });
                        toast.success('Feature Customisation Updated.',);
                        // setShowUploadProgress(false);

                        const savingComplete = () => {
                            /* if (removedDisruptorIds.current.length && removedDisruptorIds.current.length > 0) {
                                // we need to do some cleanup!
           
                                setShowCleaningUp(true);
                                cleanupFeatures(removedDisruptorIds.current, afterSaveCallback ? afterSaveCallback : exitBackToCampaignOverview);
                            } else {
    
                                if (afterSaveCallback === null) {
                
                                    exitBackToCampaignOverview();
                                } else {
                                    afterSaveCallback();
                                }
                            } */
                            afterSaveCallback?.();
                        }

                        // Save theme data if we have it, this might well move...
                        // Also, it's a bit dirty updating all themes, so this is really just a test cos we will lose themes for all other features.
                        /* if (themeData) {
                            // Process theme data into a new object, making sure any files are converted back to flat string (filename)
                            const themeDataToSave = [];
                            for (let i = 0; i < themeData.length; i++) {
                                const themeDataObj = {
                                    index: themeData[i].index,
                                    featureId: featureId,
                                    themeName: themeData[i].themeName,
                                    themeData: JSON.parse(JSON.stringify(themeData[i].themeData)),
                                }
                                for (let tab in themeDataObj.themeData) {
                                    for (let varKey in themeDataObj.themeData[tab]) {
                                        for (let varName in themeDataObj.themeData[tab][varKey]) {
                                            for (let varIndex = 0; varIndex < themeDataObj.themeData[tab][varKey][varName].length; varIndex++) {
                                                for (let prop in themeDataObj.themeData[tab][varKey][varName][varIndex]) {
                                                    if (CUSTOMISED_URL_MAP.hasOwnProperty(themeDataObj.themeData[tab][varKey][varName][varIndex][prop]?.type?.toLowerCase())) {
                                                        themeDataObj.themeData[tab][varKey][varName][varIndex][prop] = themeDataObj.themeData[tab][varKey][varName][varIndex][prop].filename;
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                                themeDataToSave.push(themeDataObj);
                            }
                            console.log('[THEMES] --- Saving theme data: ', themeData, themeDataToSave);
 
                            axios
                                .post(updateMultipleCampaignThemesRoute, {
                                    campaignID: campaignId,
                                    themes: themeDataToSave,
                                }, {withCredentials: true})
                                .then((res) => {
                                    console.log('[THEMES] --- Themes saved: ', res.data);
                                    savingComplete();
                                });
                        } else {
                            savingComplete();
                        } */

                        savingComplete();

                    })
                    .catch(function (error) {
                        // setShowUploadProgress(false);
                        toast.error('Error - ' + error);
                    });
            } else {
                // setShowUploadProgress(false);
            }
        }

        // Start uploading!
        if (!ignoreUpload) {
            // setUploadedCount(0);
            // setTotalUploads(filesToUpload.length);
            // setCurrentUploadFilename('');
            // setShowUploadProgress(true);
            await uploadFiles(
                featureId, filesToUpload, fileDirs, filesToUpload.length, afterUploadCallback
            )
            // if(filesToUpload.length == 0)   setShowUploadProgress(false);
        }
        else {
            afterUploadCallback(true);
        }
    }

    const performSave = async (featureId, featureData, customisationData, useCampaignData = null, useCustomisationData = null, onSaved = () => {}) => {
        // console.log('Performing save for feature id:', featureId, featureData, customisationData, useCustomisationData, useCampaignData, campaignId);
        // Save logic here (e.g., API call)
        setIsSaving(true);
        setSaveError(null);
        try {
            // Simulate API call with a timeout
            if (simulatedSave) {
                await new Promise((resolve) => setTimeout(resolve, 1000));
                setLastSavedFeatureId(featureId);
                //toast?.success(`${featureData?.contentLabel ?? 'Feature ' + featureId} settings saved successfully.`);
                // If there's a queued save, queue it up
                setIsSaving(false);
                onSaved?.();
            } else {
                /* The real save function */
                await saveData(featureId, () => {
                    setLastSavedFeatureId(featureId);
                    // toast?.success(`${featureData?.contentLabel ?? 'Feature ' + featureId} settings saved successfully.`);
                    setIsSaving(false);
                    onSaved?.();
                }, false, useCampaignData, useCustomisationData);
            }
        } catch (error) {
            console.log('Error saving feature customisation for feature id:', featureId, error);
            setSaveError(error);
            // toast?.error(`Error saving ${featureData?.contentLabel ?? 'Feature ' + featureId} settings.`);
            const retry = window.confirm('There was an error saving your changes. Would you like to retry?');
            if (retry) {
                performSave(featureId, featureData, customisationData, useCampaignData, useCustomisationData, onSaved);
            } else {
                setIsSaving(false);
            }
        }
    };

    const saveFeatureCustomisation = (featureId, featureData, customisationData, canForceQueuedSave = true, onSaved = () => {}) => {
        console.log('Request to save feature customisation for feature id:', featureId, featureData, customisationData);
        // If we are currenlty saving, delay this save
        if (isSaving && isSavingRef.current) {
            console.log('Currently saving, delaying save for feature id:', featureId);
            setTimeout(() =>
                saveFeatureCustomisation(featureId, featureData, customisationData, false, onSaved)
                , 1000);
            return;
        }
        // If there's a queued save for a different feature, save it immediately
        if (canForceQueuedSave && queuedFeatureId && queuedFeatureId !== featureId) {
            console.log('Different feature queued, saving queued feature first:', queuedFeatureId);
            clearTimeout(saveTimeoutRef.current);
            // performSave(queuedFeatureId, queuedFeatureData, queuedCustomisationData, featureId, featureData, customisationData);
            // setQueuedFeatureId(null);
            // setQueuedCustomisationData(null);
            // setQueuedFeatureData(null);
            forceImmediateCustomisationSave();
        } // else {
        // All saves are performed on a delay to batch rapid changes
        // We are effectively debouncing the save calls
        console.log('Queuing save for feature id:', featureId);
        clearTimeout(saveTimeoutRef.current);
        setQueuedFeatureId(featureId);
        setQueuedCustomisationData(customisationData);
        setQueuedFeatureData(featureData);
        saveTimeoutRef.current = setTimeout(() => {
            performSave(featureId, featureData, customisationData, null, null, onSaved);
            setQueuedFeatureId(null);
            setQueuedCustomisationData(null);
            setQueuedFeatureData(null);
            saveTimeoutRef.current = null;
        }, delay);
        // }
    };

    const forceImmediateCustomisationSave = async (useCampaignData = null, useCustomisationData = null) => {
        if (queuedFeatureId || queueFeatureIdRef.current) {
            console.log('Forcing immediate save for queued feature id:', queuedFeatureId, queueFeatureIdRef.current);
            clearTimeout(saveTimeoutRef.current);
            await performSave(queuedFeatureId ?? queueFeatureIdRef.current, queuedFeatureData ?? queueFeatureDataRef.current, queuedCustomisationData ?? queueCustomisationDataRef.current, useCampaignData, useCustomisationData);
            setQueuedFeatureId(null);
            setQueuedCustomisationData(null);
            setQueuedFeatureData(null);
            saveTimeoutRef.current = null;
        }
    };

    return {
        saveFeatureCustomisation: saveFeatureCustomisation,
        forceImmediateCustomisationSave: forceImmediateCustomisationSave,
        isSaving: isSaving,
        saveError: saveError,
        lastSavedFeatureId: lastSavedFeatureId,
        hasUnsavedChanges: hasUnsavedChanges,
    };
}
